/** Palco 3D do criador: o atleta num círculo de grama, girando com o arraste, em pé ou na vassoura. */
import * as THREE from "three";
import { CLIPS, SEATED, sampleClip, type Clip } from "@besenliga/anim";
import {
  addLights,
  applyPose,
  buildAthlete,
  buildBroom,
  captureRest,
  disposeAthlete,
  disposeBroom,
  gripBroom,
  loadModels,
  mountOnBroom,
  poseStanding,
  ramp,
  type AthleteModels,
  type AthleteRig,
  type AthleteSpec,
  type Broom,
  type RestPoses,
} from "@besenliga/athlete-three";
import type { Kit } from "@besenliga/shared";

export type StageMode = "pe" | "vassoura";

/** Altura do voo de exibição e enquadramento de cada modo. */
const HOVER_Y = 0.95;
const FRAMING: Record<StageMode, { target: THREE.Vector3; distance: number; elevation: number }> = {
  pe: { target: new THREE.Vector3(0, 0.8, 0), distance: 6, elevation: 0.16 },
  vassoura: { target: new THREE.Vector3(0, 0.95, 0), distance: 6, elevation: 0.24 },
};
const DRAG_SPEED = 0.012;
const SPIN_FRICTION = 4;
const KEY_STEP = Math.PI / 12;

export class AthleteStage {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 60);
  private readonly turntable = new THREE.Group();
  private readonly lookAt = FRAMING.pe.target.clone();
  private models: AthleteModels | null = null;
  private rig: AthleteRig | null = null;
  private rest: RestPoses | null = null;
  private broom: Broom | null = null;
  private holder: THREE.Group | null = null;
  private spec: AthleteSpec | null = null;
  private kit: Kit | null = null;
  private mode: StageMode = "pe";
  private action: { clip: Clip; start: number } | null = null;
  private yaw = 0.5;
  private spin = 0;
  private dragX: number | null = null;
  private dragTime = 0;
  private frame = 0;
  private last = 0;
  private readonly resizeObserver: ResizeObserver;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    const sun = addLights(this.scene, { shadowSize: 1024 });
    Object.assign(sun.shadow.camera, { left: -2.5, right: 2.5, top: 3, bottom: -2.5 });
    sun.shadow.camera.updateProjectionMatrix();
    this.scene.add(this.turntable, buildPlinth());
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerUp);
    canvas.addEventListener("keydown", this.onKeyDown);
    this.frame = requestAnimationFrame(this.tick);
  }

  async load(baseUrl: string): Promise<void> {
    this.models = await loadModels(baseUrl);
    this.rebuild();
  }

  setAthlete(spec: AthleteSpec, kit: Kit): void {
    this.spec = spec;
    this.kit = kit;
    this.rebuild();
  }

  setMode(mode: StageMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.action = null;
    this.rebuild();
  }

  /** Mostra o movimento característico da posição (só na vassoura). */
  play(clip: Clip): void {
    if (this.mode === "vassoura") this.action = { clip, start: this.last };
  }

  rotate(delta: number): void {
    this.spin = 0;
    this.yaw += delta;
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver.disconnect();
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerUp);
    this.canvas.removeEventListener("keydown", this.onKeyDown);
    this.clear();
    this.renderer.dispose();
  }

  private clear(): void {
    if (this.rig) disposeAthlete(this.rig);
    if (this.broom) disposeBroom(this.broom);
    this.holder?.removeFromParent();
    this.rig = this.rest = this.broom = this.holder = null;
  }

  private rebuild(): void {
    if (!this.models || !this.spec || !this.kit) return;
    this.clear();
    const rig = buildAthlete(this.models, this.spec, this.kit, { outline: 3 });
    this.rig = rig;
    if (this.mode === "pe") {
      poseStanding(rig.bones);
      this.turntable.add(rig.root);
      return;
    }
    this.rest = captureRest(rig.root);
    // Senta antes de montar: os apoios de pé vão para onde os pés ficam na pose sentada.
    applyPose(rig.root, this.rest, SEATED);
    this.broom = buildBroom(this.kit);
    this.holder = mountOnBroom(rig, this.broom);
    this.holder.position.y = HOVER_Y;
    this.turntable.add(this.holder);
  }

  private resize(): void {
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (w === 0 || h === 0) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    // Em telas estreitas, recua a câmera para o atleta caber inteiro.
    this.camera.userData["fit"] = Math.max(1, 0.75 / this.camera.aspect);
    this.camera.updateProjectionMatrix();
  }

  private readonly tick = (nowMs: number): void => {
    this.frame = requestAnimationFrame(this.tick);
    const now = nowMs / 1000;
    const dt = Math.min(0.05, this.last ? now - this.last : 0);
    this.last = now;

    if (this.dragX === null && this.spin !== 0) {
      this.yaw += this.spin * dt;
      this.spin *= Math.exp(-SPIN_FRICTION * dt);
      if (Math.abs(this.spin) < 0.01) this.spin = 0;
    }
    this.turntable.rotation.y = this.yaw;

    if (this.mode === "vassoura" && this.rig && this.rest && this.broom && this.holder) {
      let clip: Clip = CLIPS.sentado;
      let t = now;
      if (this.action) {
        t = now - this.action.start;
        if (t <= this.action.clip.duration) clip = this.action.clip;
        else this.action = null;
      }
      const sample = sampleClip(clip, clip === CLIPS.sentado ? now : t);
      this.holder.position.y = HOVER_Y + 0.04 * Math.sin(now * 1.8);
      this.holder.updateMatrixWorld(true);
      applyPose(this.rig.root, this.rest, sample.pose);
      gripBroom(this.rig, this.broom, sample.grip);
    }

    const frame = FRAMING[this.mode];
    this.lookAt.lerp(frame.target, 1 - Math.exp(-6 * dt));
    const fit = (this.camera.userData["fit"] as number | undefined) ?? 1;
    const d = frame.distance * fit;
    this.camera.position.set(0, this.lookAt.y + d * frame.elevation, d);
    this.camera.lookAt(this.lookAt);
    this.renderer.render(this.scene, this.camera);
  };

  private readonly onPointerDown = (e: PointerEvent): void => {
    this.dragX = e.clientX;
    this.dragTime = e.timeStamp;
    this.spin = 0;
    this.canvas.setPointerCapture(e.pointerId);
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (this.dragX === null) return;
    const dx = e.clientX - this.dragX;
    const dt = Math.max(1 / 240, (e.timeStamp - this.dragTime) / 1000);
    this.dragX = e.clientX;
    this.dragTime = e.timeStamp;
    this.yaw += dx * DRAG_SPEED;
    this.spin = Math.max(-12, Math.min(12, (dx * DRAG_SPEED) / dt));
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    this.dragX = null;
    if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
  };

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "ArrowLeft") this.rotate(-KEY_STEP);
    else if (e.key === "ArrowRight") this.rotate(KEY_STEP);
    else return;
    e.preventDefault();
  };
}

/** Círculo de grama com a marcação do centro do campo, onde o atleta fica. */
function buildPlinth(): THREE.Group {
  const g = new THREE.Group();
  const grass = new THREE.Mesh(
    new THREE.CylinderGeometry(1.25, 1.3, 0.12, 64),
    new THREE.MeshToonMaterial({ color: 0x4f9a4a, gradientMap: ramp }),
  );
  grass.position.y = -0.06;
  grass.receiveShadow = true;
  const line = new THREE.Mesh(
    new THREE.RingGeometry(0.98, 1.04, 64),
    new THREE.MeshBasicMaterial({ color: 0xf4f7ef }),
  );
  line.rotation.x = -Math.PI / 2;
  line.position.y = 0.002;
  const spot = new THREE.Mesh(new THREE.CircleGeometry(0.05, 24), line.material);
  spot.rotation.x = -Math.PI / 2;
  spot.position.y = 0.002;
  const shade = new THREE.Mesh(
    new THREE.CircleGeometry(1.2, 64),
    new THREE.ShadowMaterial({ opacity: 0.28 }),
  );
  shade.rotation.x = -Math.PI / 2;
  shade.position.y = 0.004;
  shade.receiveShadow = true;
  g.add(grass, line, spot, shade);
  return g;
}
